import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Workspaces as WorkspacesApi } from '@air/api';
import { type Asset, type ShortUrlResponse, type WorkspaceInfo } from '@air/api/types';
// biome-ignore lint/plugin/no-air-types-import: ShortUrlObjectType must match @air/api client enums until SDK paths align
import { ShortUrlObjectType as ShortUrlObjectTypeForList } from '@air/types';
import { type ApiResponse } from '@air/utils-api';
import { isAirrorInstance } from '@air/utils-error';

export const DEFAULT_ASSET_SHARE_LINKS_LIST_LIMIT = 100;

type AssetShareLinksListItemApi = ApiResponse<typeof WorkspacesApi.ShortUrl.list>['data'][number];

type AssetScopedShareLinkListItemApi = Extract<
  AssetShareLinksListItemApi,
  { objectType: ShortUrlObjectTypeForList.asset | ShortUrlObjectTypeForList.clip }
>;

/**
 * List row for share-modal use: `ShortUrlResponse` fields plus list/slot metadata.
 *
 * Runtime payloads from `ShortUrl.list` already match get-existing / `ShortUrlResponse`
 * (including presentation settings). The cast bridges incompatible TypeScript shapes:
 * server-sdk list types (`PresentationSettingsOutput`, local `ViewTypeName` string union)
 * vs `@air/api/types` `ShortUrlResponse` (enum-based `ViewType`). This goes away once
 * short-url endpoints are fully on server-sdk and consumers stop depending on the
 * legacy `@air/api/types` / shared-types short-url models.
 */
export type AssetScopedShareLinkListItem = ShortUrlResponse & {
  objectType: ShortUrlObjectTypeForList.asset | ShortUrlObjectTypeForList.clip;
  objectId: string;
  version?: number;
  targetClipId?: string | null;
  name?: string;
  title?: string;
  workspaceId?: string;
  roleId?: string;
};

export type AssetShareLinkListAssetItem = AssetScopedShareLinkListItem & {
  objectType: ShortUrlObjectTypeForList.asset;
};

export type AssetShareLinkListClipItem = AssetScopedShareLinkListItem & {
  objectType: ShortUrlObjectTypeForList.clip;
};

const reshapeAssetScopedShareLinkListItem = (item: AssetScopedShareLinkListItemApi): AssetScopedShareLinkListItem =>
  item as AssetScopedShareLinkListItem;

export type UseListAssetShareLinksParams = {
  workspaceId: WorkspaceInfo['id'] | undefined;
  assetId: Asset['id'] | undefined;
  limit?: number;
};

export const ASSET_SHARE_LINKS_LIST = 'ASSET_SHARE_LINKS_LIST';
export type AssetShareLinksListKey = [
  typeof ASSET_SHARE_LINKS_LIST,
  {
    workspaceId: WorkspaceInfo['id'] | undefined;
    assetId: Asset['id'] | undefined;
    limit: number;
  },
];

export const getAssetShareLinksListKey = (params: UseListAssetShareLinksParams): AssetShareLinksListKey => [
  ASSET_SHARE_LINKS_LIST,
  {
    workspaceId: params.workspaceId,
    assetId: params.assetId,
    limit: params.limit ?? DEFAULT_ASSET_SHARE_LINKS_LIST_LIMIT,
  },
];

export type UseListAssetShareLinksOptions = {
  enabled?: boolean;
};

export const useListAssetShareLinks = (
  params: UseListAssetShareLinksParams,
  options?: UseListAssetShareLinksOptions,
) => {
  const workspaceId = params.workspaceId;
  const assetId = params.assetId;
  const limit = params.limit ?? DEFAULT_ASSET_SHARE_LINKS_LIST_LIMIT;

  const query = useInfiniteQuery({
    queryKey: getAssetShareLinksListKey(params),
    queryFn:
      workspaceId && assetId
        ? async ({ pageParam }) => {
            const result = await WorkspacesApi.ShortUrl.list({
              workspaceId,
              objectType: ShortUrlObjectTypeForList.asset,
              assetId,
              limit,
              cursor: pageParam || undefined,
            });

            if (isAirrorInstance(result)) {
              throw result;
            }

            return result;
          }
        : skipToken,
    enabled: !!workspaceId && !!assetId && (options?.enabled ?? true),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
    retry: false,
  });

  const flattenedData = useMemo(
    () => query.data?.pages.flatMap((page) => (page.data ?? []).map(reshapeAssetScopedShareLinkListItem)) ?? [],
    [query.data],
  );

  const data = useMemo(
    () =>
      query.data
        ? {
            data: flattenedData,
            total: query.data.pages[0]?.total ?? 0,
            pagination: query.data.pages[query.data.pages.length - 1]?.pagination ?? {
              cursor: null,
              hasMore: false,
            },
          }
        : undefined,
    [query.data, flattenedData],
  );

  return {
    data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    isError: query.isError,
    refetch: query.refetch,
  };
};
